/**
 * Árbol de rutas.
 *
 * Cómo agregar una vista nueva
 * ----------------------------
 * Creá `routes/<modulo>/routes.tsx` exportando `moduleRoutes` (el contrato
 * está en `routes/module.ts`). **No se toca este archivo ni `layout/nav.ts`:**
 * el módulo se descubre solo y su entrada del menú se activa sola.
 *
 * Es deliberado. Los diez módulos de B6 se construyen en paralelo, y un árbol
 * escrito a mano convierte a este archivo y a `nav.ts` en los dos únicos
 * lugares donde todas las ramas chocan.
 *
 * Guardas:
 *
 *   <Route element={<RequireAuth />}>              sesión obligatoria
 *     <Route element={<RequireRole minimum="analyst" />}>   rol mínimo
 *
 * `RequireRole` envuelve **la rama**, no cada componente: la guarda vive en
 * la definición de la ruta y así se lee de un vistazo quién entra a qué. Un
 * `if (!can('admin')) return null` adentro del componente es invisible desde
 * acá y se olvida en la siguiente vista. Un módulo la declara con
 * `minimumRole` en su `moduleRoutes`.
 *
 * Las rutas de módulos pendientes existen igual y muestran el placeholder:
 * si alguien pega una URL de un módulo que aún no está, tiene que ver qué
 * issue lo construye, no un 404 que parece un bug.
 */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth, RequireRole } from '@/auth/guards';
import { AppShell } from '@/layout/AppShell';
import { HOME_PATH, NAV_MODULES } from '@/layout/nav';
import { AccountPage } from './AccountPage';
import { LoginPage } from './LoginPage';
import type { ModuleRoutes } from './module';
import { ModulePlaceholder } from './ModulePlaceholder';
import { NotFoundPage } from './NotFoundPage';
import { OverviewPage } from './OverviewPage';

// Vite resuelve el glob en tiempo de compilación: no hay lectura de disco ni
// registro dinámico en runtime, así que el mecanismo no agrega peso al bundle.
const REGISTERED = import.meta.glob<{ moduleRoutes?: ModuleRoutes }>(
  './*/routes.tsx',
  { eager: true },
);

const MODULES: ModuleRoutes[] = Object.values(REGISTERED)
  .map((mod) => mod.moduleRoutes)
  .filter((mod): mod is ModuleRoutes => Boolean(mod));

const REGISTERED_IDS = new Set(MODULES.map((mod) => mod.moduleId));

/** Los que `nav.ts` declara y todavía nadie registró. */
const PENDING_MODULES = NAV_MODULES.filter(
  (module) => module.status === 'pending' && !REGISTERED_IDS.has(module.id),
);

function moduleBranch(mod: ModuleRoutes): ReactNode {
  const nav = NAV_MODULES.find((entry) => entry.id === mod.moduleId);
  if (!nav) {
    // Un módulo registrado sin entrada en el menú sería una pantalla
    // inalcanzable. Mejor no montarla que montar algo que nadie encuentra.
    return null;
  }

  const branch = (
    <Route key={mod.moduleId} path={nav.path}>
      {mod.routes.map((route) =>
        route.path === '' ? (
          <Route key="__index" index element={route.element} />
        ) : (
          <Route key={route.path} path={route.path} element={route.element} />
        ),
      )}
    </Route>
  );

  if (!mod.minimumRole) return branch;
  return (
    <Route key={`${mod.moduleId}__guard`} element={<RequireRole minimum={mod.minimumRole} />}>
      {branch}
    </Route>
  );
}

export function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to={HOME_PATH} replace />} />
          <Route path="/overview" element={<OverviewPage />} />
          {/* No es un módulo del menú: se llega desde el nombre propio en la
              barra superior, para cualquier rol (#192). */}
          <Route path="/account" element={<AccountPage />} />
          {/* El stock pasó a ser una pestaña de Etiquetas (#170). El link
              viejo sigue llegando. */}
          <Route path="/hq/stock" element={<Navigate to="/labels/stock" replace />} />

          {MODULES.map(moduleBranch)}

          {PENDING_MODULES.map((module) => (
            <Route
              key={module.id}
              path={`${module.path}/*`}
              element={<ModulePlaceholder module={module} />}
            />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
